import React from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { useThemeStore } from '../../../src/stores/themeStore';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme, ThemeMode, FontSizeScale } from '../../../src/theme/types';
import ScreenHeader from '../../../src/components/ScreenHeader';

const THEME_OPTIONS: { key: ThemeMode; label: string; icon: string }[] = [
  { key: 'system', label: 'System', icon: '\u{1F4F1}' },
  { key: 'light', label: 'Light', icon: '\u2600\uFE0F' },
  { key: 'dark', label: 'Dark', icon: '\u{1F319}' },
];

const FONT_SIZE_OPTIONS: { key: FontSizeScale; label: string; previewSize: number }[] = [
  { key: 'small', label: 'Small', previewSize: 13 },
  { key: 'default', label: 'Default', previewSize: 16 },
  { key: 'large', label: 'Large', previewSize: 19 },
  { key: 'xlarge', label: 'Extra Large', previewSize: 22 },
];

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  content: { padding: 16, paddingBottom: 40, gap: 24 },
  sectionTitle: {
    fontSize: theme.fs(14),
    fontWeight: '700' as const,
    color: theme.colors.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  card: {
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    overflow: 'hidden' as const,
    ...theme.shadows.sm,
  },
  cardContent: { padding: 16 },
  cardLabel: {
    fontSize: theme.fs(15),
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    marginBottom: 12,
  },
  themeRow: {
    flexDirection: 'row' as const,
    gap: 8,
  },
  chip: {
    flex: 1,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: theme.colors.bgTertiary,
    gap: 6,
  },
  chipActive: {
    backgroundColor: theme.colors.primaryBg,
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  themeChipIcon: { fontSize: 16 },
  chipText: {
    fontSize: theme.fs(13),
    fontWeight: '500' as const,
    color: theme.colors.textMuted,
  },
  chipTextActive: {
    color: theme.colors.primary,
    fontWeight: '600' as const,
  },
  fontSizeRow: {
    flexDirection: 'row' as const,
    gap: 8,
  },
  fontSizeChip: {
    flex: 1,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: theme.colors.bgTertiary,
    gap: 2,
  },
  fontSizePreviewLetter: {
    fontWeight: '700' as const,
    color: theme.colors.textSecondary,
  },
  fontSizePreviewLetterActive: {
    color: theme.colors.primary,
  },
  fontSizeLabel: {
    fontSize: theme.fs(11),
    color: theme.colors.textMuted,
  },
  fontSizeLabelActive: {
    color: theme.colors.primary,
    fontWeight: '600' as const,
  },
  previewContainer: {
    marginTop: 16,
    padding: 12,
    borderRadius: 8,
    backgroundColor: theme.colors.bgTertiary,
  },
  previewText: {
    fontSize: theme.fs(15),
    color: theme.colors.textPrimary,
    lineHeight: theme.fs(22),
  },
});

export default function PersonalizeScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);
  const fontSizeScale = useThemeStore((s) => s.fontSizeScale);
  const setFontSizeScale = useThemeStore((s) => s.setFontSizeScale);

  return (
    <View style={styles.container}>
      <ScreenHeader
        title="Personalize"
        left={{
          label: 'Go back',
          icon: 'arrow-back',
          onPress: () => router.back(),
          testID: 'personalize.back-button',
        }}
      />
      <ScrollView contentContainerStyle={styles.content}>
        {/* Theme */}
        <View>
          <Text style={styles.sectionTitle}>Appearance</Text>
          <View style={styles.card}>
            <View style={styles.cardContent}>
              <Text style={styles.cardLabel}>Theme</Text>
              <View style={styles.themeRow} accessibilityRole="radiogroup" accessibilityLabel="Theme">
                {THEME_OPTIONS.map((opt) => (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.chip, themeMode === opt.key && styles.chipActive]}
                    onPress={() => setThemeMode(opt.key)}
                    accessibilityRole="button"
                    accessibilityState={{ checked: themeMode === opt.key }}
                    accessibilityLabel={`${opt.label} theme`}
                    testID={`personalize.theme-${opt.key}`}
                  >
                    <Text style={styles.themeChipIcon} accessible={false}>{opt.icon}</Text>
                    <Text
                      style={[styles.chipText, themeMode === opt.key && styles.chipTextActive]}
                      accessible={false}
                    >
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>
        </View>

        {/* Font Size */}
        <View>
          <Text style={styles.sectionTitle}>Text Size</Text>
          <View style={styles.card}>
            <View style={styles.cardContent}>
              <Text style={styles.cardLabel}>Font Size</Text>
              <View style={styles.fontSizeRow} accessibilityRole="radiogroup" accessibilityLabel="Font size">
                {FONT_SIZE_OPTIONS.map((opt) => (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.fontSizeChip, fontSizeScale === opt.key && styles.chipActive]}
                    onPress={() => setFontSizeScale(opt.key)}
                    accessibilityRole="button"
                    accessibilityState={{ checked: fontSizeScale === opt.key }}
                    accessibilityLabel={`${opt.label} text size`}
                    testID={`personalize.font-size-${opt.key}`}
                  >
                    <Text
                      style={[
                        styles.fontSizePreviewLetter,
                        { fontSize: opt.previewSize },
                        fontSizeScale === opt.key && styles.fontSizePreviewLetterActive,
                      ]}
                      accessible={false}
                    >
                      Aa
                    </Text>
                    <Text
                      style={[styles.fontSizeLabel, fontSizeScale === opt.key && styles.fontSizeLabelActive]}
                      accessible={false}
                    >
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              <View style={styles.previewContainer}>
                <Text style={styles.previewText}>
                  The quick brown fox jumps over the lazy dog.
                </Text>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}
